import * as RadixNav from '@radix-ui/react-navigation-menu';
import { NavLink, useLocation, type LinkProps } from 'react-router-dom';
import { HamburgerMenuIcon } from '@radix-ui/react-icons';
import { Drawer } from '@scholastic/mockingjay';
import * as Collapsible from '@radix-ui/react-collapsible';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cx } from 'class-variance-authority';
import { debounce } from '../../utils/debounce';
import Caret from '../../assets/icons/caretDown.svg';

export interface ProductNavDropdownProps {
  children?: ReactNode;
  label?: string;
  /** Nested within More Dropdown */
  nested?: boolean;
}

export interface ProductNavLinkProps extends LinkProps {
  /** Nested within More Dropdown */
  nested?: boolean;
}

export interface ProductNavLogoProps {
  src?: string;
  alt?: string;
  href: LinkProps['to'];
}

interface NavProps {
  logo: ReactNode;
  childrenWithoutLogo: ReactNode;
  className?: string;
}

interface ProductNavProps {
  className?: string;
  children: ReactNode;
  isDark?: boolean;
}

interface NavContextProps {
  isDark?: boolean;
}

const NavContext = createContext<NavContextProps>({
  isDark: false,
});

interface TabletContextProps {
  sliceIndex: number;
}
const TabletContext = createContext<TabletContextProps>({
  sliceIndex: -1,
});

function NavProvider({ children, isDark }: PropsWithChildren<NavContextProps>) {
  const value = useMemo(() => ({ isDark }), [isDark]);
  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

function TabletProvider({
  children,
  sliceIndex,
}: PropsWithChildren<TabletContextProps>) {
  const value = useMemo(() => ({ sliceIndex }), [sliceIndex]);
  return (
    <TabletContext.Provider value={value}>{children}</TabletContext.Provider>
  );
}
function useTablet() {
  return useContext(TabletContext);
}
function useNav() {
  return useContext(NavContext);
}

export function ProductNavLogo({ alt, src, href }: ProductNavLogoProps) {
  const logo = src && (
    <img src={src} alt={alt} className=" min-w-1 max-w-[157px]" />
  );

  return (
    <>
      {/* mobile */}
      <MediaQuery maxWidth={639}>
        <NavLink
          to={href}
          className={cx('focus-ring', {
            'text-blue-700': !src,
          })}
          end
        >
          {logo}
          {!src && alt}
        </NavLink>
      </MediaQuery>

      {/* tablet */}
      <MediaQuery minWidth={640}>
        <RadixNav.Item className="shrink-0">
          <RadixNav.Link asChild>
            <NavLink
              to={href}
              className={cx('mr-6 block bg-transparent focus-ring', {
                'text-blue-700': !src,
              })}
              end
            >
              {logo}
              {!src && alt}
            </NavLink>
          </RadixNav.Link>
        </RadixNav.Item>
      </MediaQuery>
    </>
  );
}

function useProductNavChildren(children: ReactNode) {
  return useMemo(() => {
    let logo;
    let childrenWithoutLogo: ReactNode[] = [];

    Children.toArray(children).forEach((child) => {
      if (isValidElement(child) && child.type === ProductNavLogo) {
        logo = child;
      } else {
        childrenWithoutLogo = [...childrenWithoutLogo, child];
      }
    });

    return { logo, childrenWithoutLogo };
  }, [children]);
}

function MobileNav({ logo, childrenWithoutLogo, className }: NavProps) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const location = useLocation();
  const { isDark } = useNav();

  // Close the drawer when the location changes
  useEffect(() => {
    setOpenDrawer(false);
  }, [location]);

  return (
    <div className={cx('flex items-center gap-3 p-4', className)}>
      <Drawer
        open={openDrawer}
        onOpenChange={setOpenDrawer}
        size="medium"
        direction="left"
        title="Product Navigation"
        trigger={
          <button
            type="button"
            aria-label="Navigation"
            className="rounded-full p-[10px] focus-ring"
          >
            <HamburgerMenuIcon
              className={`size-6 ${
                isDark ? 'text-brand-white' : 'text-neutral-1300'
              }`}
              aria-hidden="true"
            />
          </button>
        }
      >
        {childrenWithoutLogo}
      </Drawer>
      {logo}
    </div>
  );
}

function calculateAvailableWidth(
  containerWidth: number,
  containerPadding: number,
  logoWidth: number
) {
  return containerWidth - containerPadding * 2 - logoWidth;
}

function getSliceIndex(
  breakpoints: number[],
  availableWidth: number,
  requiredWidth: number
) {
  const showMoreDropdown = availableWidth <= requiredWidth;
  const moreDropdownWidth = showMoreDropdown ? 123 : 0;
  return showMoreDropdown
    ? breakpoints.findLastIndex(
        (bp) => bp <= availableWidth - moreDropdownWidth
      ) + 1
    : -1;
}

function usePriorityNav(logo: ReactNode) {
  const containerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLUListElement>(null);
  const [sliceIndex, setSliceIndex] =
    useState<TabletContextProps['sliceIndex']>(-1);
  const requiredWidth = useRef(0);
  const widthBreakpoints = useRef<number[]>([]);
  const isMobile = useMediaQuery({ query: '(max-width: 639px)' });
  const calcSpace = useCallback(() => {
    if (containerRef.current) {
      const availableWidth = calculateAvailableWidth(
        containerRef.current.clientWidth,
        parseFloat(window.getComputedStyle(containerRef.current).paddingInline),
        logo ? 181 : 0
      );

      const lastIndexThatFits = getSliceIndex(
        widthBreakpoints.current,
        availableWidth,
        requiredWidth.current
      );
      setSliceIndex(lastIndexThatFits);
    }
  }, [logo]);

  useEffect(() => {
    const debouncedCalcSpace = debounce(calcSpace, 100);

    const containerObserver = new ResizeObserver(debouncedCalcSpace);
    const navObserver = new ResizeObserver(
      debounce(() => {
        if (navRef.current && requiredWidth.current === 0) {
          Array.from(navRef.current.children)
            .slice(logo ? 1 : 0) // exclude logo
            .forEach((child) => {
              requiredWidth.current += child.clientWidth;
              widthBreakpoints.current.push(requiredWidth.current);
            });
        }
        calcSpace();
      }, 100)
    );
    // container resize listener
    if (containerRef.current) {
      containerObserver.observe(containerRef.current);
    }
    // nav item resize listener
    if (navRef.current) {
      navObserver.observe(navRef.current.children[0]!);
    }
    return () => {
      containerObserver.disconnect();
      navObserver.disconnect();
    };
  }, [calcSpace, logo, isMobile, containerRef]);

  return { containerRef, navRef, sliceIndex };
}

function TabletNav({ logo, childrenWithoutLogo, className }: NavProps) {
  const { containerRef, navRef, sliceIndex } = usePriorityNav(logo);
  return (
    <div
      ref={containerRef}
      className={cx('flex items-center gap-y-0.5 px-10 py-4', className)}
    >
      <RadixNav.Root className="relative z-[1] flex grow">
        <RadixNav.List
          className="flex h-full w-full items-center text-center"
          ref={navRef}
        >
          <TabletProvider sliceIndex={sliceIndex}>
            {logo}
            {childrenWithoutLogo}
          </TabletProvider>

          <RadixNav.Indicator className="top-full z-[1] flex h-[10px] items-end justify-center overflow-hidden">
            <div className="relative top-[80%] size-3.5 rotate-[45deg] rounded-tl-[2px] border bg-brand-white" />
          </RadixNav.Indicator>
        </RadixNav.List>
      </RadixNav.Root>
    </div>
  );
}

export function ProductNav({
  children,
  className,
  isDark = false,
}: ProductNavProps) {
  const { logo, childrenWithoutLogo } = useProductNavChildren(children);

  return (
    <NavProvider isDark={isDark}>
      {/* mobile */}
      <MediaQuery maxWidth={639}>
        <MobileNav
          logo={logo}
          childrenWithoutLogo={childrenWithoutLogo}
          className={className}
        />
      </MediaQuery>

      {/* tablet */}
      <MediaQuery minWidth={640}>
        <TabletNav
          logo={logo}
          childrenWithoutLogo={childrenWithoutLogo}
          className={className}
        />
      </MediaQuery>
    </NavProvider>
  );
}

export function ProductNavSublink({
  nested,
  className,
  ...props
}: ProductNavLinkProps) {
  const isMobile = useMediaQuery({ query: '(max-width: 639px)' });
  const link = (
    <NavLink
      {...props}
      className={cx(
        'px-4 py-3 text-base text-neutral-900 focus-ring hover:bg-neutral-100',
        {
          'text-left': !isMobile,
          'ml-4': !isMobile && nested,
        },
        className
      )}
      end
    />
  );

  return isMobile ? link : <RadixNav.Link asChild>{link}</RadixNav.Link>;
}

export function ProductNavDropdown({
  children,
  label,
  nested,
}: ProductNavDropdownProps) {
  const { isDark } = useNav();

  return (
    <>
      {/* mobile */}
      <MediaQuery maxWidth={639}>
        <Collapsible.Root defaultOpen>
          <Collapsible.Trigger className="flex items-center gap-3 text-lg text-neutral-1300 focus-ring hover:bg-neutral-100">
            {label}
            <Caret
              aria-hidden
              className="transition-all [&[data-state=open]>svg]:rotate-180"
            />
          </Collapsible.Trigger>
          <Collapsible.Content className="mt-2 flex w-full flex-col gap-3 px-4 text-neutral-900">
            {children}
          </Collapsible.Content>
        </Collapsible.Root>
      </MediaQuery>

      {/* tablet */}
      <MediaQuery minWidth={640}>
        {nested ? (
          <div>
            <div
              className={`mb-2 text-left text-lg text-neutral-1300 focus-ring `}
            >
              {label}
            </div>
            <div className="flex flex-col gap-3">
              {Children.map(children, (child) => {
                if (isValidElement(child) && child.type === ProductNavSublink) {
                  return cloneElement(
                    child as ReactElement<ProductNavLinkProps>,
                    {
                      nested,
                    }
                  );
                }
                return child;
              })}
            </div>
          </div>
        ) : (
          <RadixNav.Item>
            <RadixNav.Trigger
              className={`flex items-center gap-3 whitespace-nowrap rounded px-6 py-2 text-xl ${
                isDark
                  ? 'hover:data-[state=open]:bg-opacity/35 text-brand-white data-[state=open]:bg-brand-black'
                  : 'bg-brand-white text-neutral-1300 data-[state=open]:bg-brand-grey_7 hover:data-[state]:bg-neutral-100'
              }`}
              aria-controls={undefined}
            >
              {label}
              <Caret
                aria-hidden
                className={`transition-all [&[data-state=open]>svg]:rotate-180 ${
                  isDark ? 'text-brand-white' : 'text-neutral-1300'
                }`}
              />
            </RadixNav.Trigger>
            <div className="relative">
              <RadixNav.Content
                aria-labelledby={undefined}
                role="menu"
                className="absolute left-1/2 mt-2 flex w-auto -translate-x-1/2 flex-col gap-3 truncate rounded border border-neutral-200 bg-brand-white px-4 py-3 shadow-xl"
              >
                {children}
              </RadixNav.Content>
            </div>
          </RadixNav.Item>
        )}
      </MediaQuery>
    </>
  );
}

export function ProductNavList({ children }: PropsWithChildren) {
  const { sliceIndex } = useTablet();
  const sliced = sliceIndex > -1;

  return (
    <>
      {/* mobile */}
      <MediaQuery maxWidth={639}>
        <nav className="flex flex-col gap-6 p-2">{children}</nav>
      </MediaQuery>

      {/* tablet */}
      <MediaQuery minWidth={640}>
        {sliced ? Children.toArray(children).slice(0, sliceIndex) : children}
        {sliced && (
          <ProductNavDropdown label="More">
            {Children.toArray(children)
              .slice(sliceIndex)
              .map((child) => {
                return cloneElement(
                  child as ReactElement<ProductNavDropdownProps>,
                  { nested: true }
                );
              })}
          </ProductNavDropdown>
        )}
      </MediaQuery>
    </>
  );
}

export function ProductNavLink({ nested, ...props }: ProductNavLinkProps) {
  const { isDark } = useNav();

  return (
    <>
      {/* mobile */}
      <MediaQuery maxWidth={639}>
        <NavLink
          {...props}
          className="text-lg text-neutral-1300 focus-ring hover:bg-neutral-100"
          end
        />
      </MediaQuery>

      {/* tablet */}
      <MediaQuery minWidth={640}>
        {nested ? (
          <RadixNav.Link asChild>
            <NavLink
              {...props}
              className="gap-3 text-left text-lg text-neutral-1300 focus-ring"
              end
            />
          </RadixNav.Link>
        ) : (
          <RadixNav.Item>
            <RadixNav.Link
              className={`block whitespace-nowrap rounded-sm px-3 py-2 text-xl focus-ring  ${
                isDark
                  ? 'bg-transparent text-brand-white hover:bg-black hover:bg-opacity-[0.35]'
                  : 'bg-brand-white text-neutral-1100 hover:bg-neutral-100'
              }`}
              asChild
            >
              <NavLink {...props} end />
            </RadixNav.Link>
          </RadixNav.Item>
        )}
      </MediaQuery>
    </>
  );
}
